// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: geometry for the logo loader. The two halves of the mark loosen into
// round-capped arcs, spin, and settle back into the mark. Each half's outline
// is sampled into a fixed number of points so an arc can blend into its half
// point by point instead of crossfading. The boot splash in index.html runs
// the same maths inline, since it paints before any module loads; keep the two
// in step.

export type MarkHalf = 'violet' | 'apricot';

export const MARK_HALVES: ReadonlyArray<MarkHalf> = ['violet', 'apricot'];

export const MARK_COLORS: Record<MarkHalf, string> = {violet: '#9981F4', apricot: '#FFA16F'};

export const MARK_PATHS: Record<MarkHalf, string> = {
	violet:
		'M239 73 C249 67 262 75 262 88 L262 145 C262 163 252 176 234 181 C191 193 166 225 168 259 C169 283 180 303 195 319 C200 325 195 331 188 329 L132 311 C105 302 90 282 90 253 L90 197 C90 170 103 150 125 136 Z',
	apricot:
		'M287 170 C281 164 284 158 293 158 C365 155 422 199 422 261 L422 310 C422 338 409 358 384 373 L273 439 C261 446 249 439 249 425 L249 369 C249 349 260 334 279 328 C326 313 345 282 342 249 C340 219 320 189 287 170 Z',
};

type Point = [number, number];

// The mark sits in a 512 box and is point-symmetric about its centre.
const CENTER = 256;
// An arc this radius, length and thickness nearly covers one half of the mark.
const RADIUS = 127;
const REST_SWEEP = 139;
const REST_WIDTH = 78;
const SPIN_WIDTH = 44;
const MID_ANGLE: Record<MarkHalf, number> = {violet: 202.5, apricot: 22.5};
// While spinning, each arc grows mostly from its leading end.
const REACH = 0.3;

export const LOOP_SECONDS = 4.6;
const LOOP_START = 0.2;

const clamp = (value: number, min = 0, max = 1) => Math.min(max, Math.max(min, value));
const lerp = (a: number, b: number, p: number) => a + (b - a) * p;
const span = (t: number, a: number, b: number) => clamp((t - a) / (b - a));
const easeInOut = (p: number) => (p < 0.5 ? 4 * p * p * p : 1 - (-2 * p + 2) ** 3 / 2);
const toRadians = (degrees: number) => (degrees * Math.PI) / 180;

// Rotation that accelerates from rest, cruises, and eases to rest, turning exactly `total` degrees.
export function spinProfile(accel: number, cruise: number, decel: number, total: number): (t: number) => number {
	const speed = total / (accel / 3 + cruise + decel / 3);
	return (t) => {
		if (t <= 0) return 0;
		if (t < accel) return (speed * t ** 3) / (3 * accel * accel);
		const afterAccel = (speed * accel) / 3;
		if (t < accel + cruise) return afterAccel + speed * (t - accel);
		const s = clamp((t - accel - cruise) / decel);
		return afterAccel + speed * cruise + (speed * decel * (1 - (1 - s) ** 3)) / 3;
	};
}

// Flattens an absolute M/L/C/Z path and resamples it into `count` evenly spaced points.
export function samplePath(d: string, count: number): Array<Point> {
	const tokens = d.match(/[MLCZ]|-?\d*\.?\d+/g) ?? [];
	const dense: Array<Point> = [];
	let index = 0;
	let command = '';
	let current: Point = [0, 0];
	let start: Point = [0, 0];
	const next = () => Number(tokens[index++]);
	while (index < tokens.length) {
		if (/[MLCZ]/.test(tokens[index])) command = tokens[index++];
		if (command === 'M') {
			current = [next(), next()];
			start = current;
			dense.push(current);
		} else if (command === 'L') {
			current = [next(), next()];
			dense.push(current);
		} else if (command === 'C') {
			const c1: Point = [next(), next()];
			const c2: Point = [next(), next()];
			const end: Point = [next(), next()];
			const from = current;
			for (let step = 1; step <= 24; step++) {
				const u = step / 24;
				const v = 1 - u;
				dense.push([
					v ** 3 * from[0] + 3 * v * v * u * c1[0] + 3 * v * u * u * c2[0] + u ** 3 * end[0],
					v ** 3 * from[1] + 3 * v * v * u * c1[1] + 3 * v * u * u * c2[1] + u ** 3 * end[1],
				]);
			}
			current = end;
		} else if (command === 'Z') {
			dense.push(start);
			current = start;
		}
	}
	const lengths = [0];
	for (let i = 1; i < dense.length; i++) {
		lengths.push(lengths[i - 1] + Math.hypot(dense[i][0] - dense[i - 1][0], dense[i][1] - dense[i - 1][1]));
	}
	const total = lengths[lengths.length - 1];
	const points: Array<Point> = [];
	let segment = 1;
	for (let i = 0; i < count; i++) {
		const target = (total * i) / count;
		while (segment < lengths.length - 1 && lengths[segment] < target) segment++;
		const a = dense[segment - 1];
		const b = dense[segment];
		const f = (target - lengths[segment - 1]) / (lengths[segment] - lengths[segment - 1] || 1);
		points.push([lerp(a[0], b[0], f), lerp(a[1], b[1], f)]);
	}
	return points;
}

// The outline of a thick round-capped arc, sampled evenly and clockwise from the outer edge at its start angle.
export function arcOutline(start: number, sweep: number, width: number, count: number): Array<Point> {
	const half = width / 2;
	const outer = RADIUS + half;
	const inner = Math.max(0, RADIUS - half);
	const a0 = toRadians(start);
	const d = toRadians(Math.max(sweep, 0.01));
	const lengths = [outer * d, Math.PI * half, inner * d, Math.PI * half];
	const total = lengths[0] + lengths[1] + lengths[2] + lengths[3];
	const points: Array<Point> = [];
	for (let i = 0; i < count; i++) {
		let s = (total * i) / count;
		let seg = 0;
		while (seg < 3 && s > lengths[seg]) s -= lengths[seg++];
		const f = lengths[seg] ? s / lengths[seg] : 0;
		if (seg === 0 || seg === 2) {
			const a = seg === 0 ? a0 + d * f : a0 + d * (1 - f);
			const r = seg === 0 ? outer : inner;
			points.push([CENTER + r * Math.cos(a), CENTER + r * Math.sin(a)]);
		} else {
			const a = seg === 1 ? a0 + d : a0;
			const cap = seg === 1 ? a + Math.PI * f : a + Math.PI + Math.PI * f;
			points.push([
				CENTER + RADIUS * Math.cos(a) + half * Math.cos(cap),
				CENTER + RADIUS * Math.sin(a) + half * Math.sin(cap),
			]);
		}
	}
	return points;
}

const restStart = (half: MarkHalf) => MID_ANGLE[half] - REST_SWEEP / 2;

// A half's outline samples, rotated in index so sample i sits near sample i of the resting arc.
const alignedCache = new Map<string, Array<Point>>();
export function alignedHalf(half: MarkHalf, count: number): Array<Point> {
	const key = `${half}:${count}`;
	const cached = alignedCache.get(key);
	if (cached) return cached;
	const raw = samplePath(MARK_PATHS[half], count);
	const arc = arcOutline(restStart(half), REST_SWEEP, REST_WIDTH, count);
	let best = 0;
	let bestCost = Infinity;
	for (let shift = 0; shift < count; shift++) {
		let cost = 0;
		for (let i = 0; i < count; i += 2) {
			const [x, y] = raw[(i + shift) % count];
			cost += (x - arc[i][0]) ** 2 + (y - arc[i][1]) ** 2;
		}
		if (cost < bestCost) {
			bestCost = cost;
			best = shift;
		}
	}
	const aligned = raw.map((_, i) => raw[(i + best) % count]);
	alignedCache.set(key, aligned);
	return aligned;
}

export interface MarkFrame {
	// Degrees clockwise; always a whole number of turns when the mark is at rest.
	rotation: number;
	// 0 = the logo, 1 = two spinner arcs.
	loose: number;
	// Arc length of each half while loose, in degrees; they breathe half a beat apart.
	sweep: [number, number];
	scale: number;
}

const breath = (t: number, i: number) => 108 + 44 * Math.sin((2 * Math.PI * t) / 1.15 + i * Math.PI);
const loopSpin = spinProfile(0.7, 1.6, 1.15, 720);

// One cycle of the loader: the mark loosens, spins twice, settles back, and rests for a moment.
export function loaderFrame(seconds: number): MarkFrame {
	const t = ((seconds % LOOP_SECONDS) + LOOP_SECONDS) % LOOP_SECONDS;
	const loose = easeInOut(span(t, LOOP_START, LOOP_START + 0.6)) * (1 - easeInOut(span(t, 3.0, 3.65)));
	const settle = Math.sin(Math.PI * span(t, 3.55, 4.05));
	return {
		rotation: loopSpin(t - LOOP_START),
		loose,
		sweep: [breath(t - LOOP_START, 0), breath(t - LOOP_START, 1)],
		scale: 1 + 0.025 * settle,
	};
}

// The SVG path for one half at a frame, or null when the half is exactly the logo shape (use MARK_PATHS).
export function halfPath(half: MarkHalf, frame: MarkFrame, count: number): string | null {
	const blend = easeInOut(clamp(frame.loose * 1.25));
	if (blend < 0.001) return null;
	const i = half === 'violet' ? 0 : 1;
	const sweep = lerp(REST_SWEEP, frame.sweep[i], frame.loose);
	const width = lerp(REST_WIDTH, SPIN_WIDTH, frame.loose);
	const arc = arcOutline(restStart(half) - (sweep - REST_SWEEP) * REACH, sweep, width, count);
	const shape = alignedHalf(half, count);
	let d = '';
	for (let p = 0; p < count; p++) {
		d += `${p ? 'L' : 'M'}${lerp(shape[p][0], arc[p][0], blend).toFixed(1)} ${lerp(shape[p][1], arc[p][1], blend).toFixed(1)}`;
	}
	return `${d}Z`;
}
